/* eslint-disable */
import { Modal } from '@mui/material';
import { CheckPicker } from 'rsuite';
import { useSelector, useDispatch } from 'react-redux';
import { modalActions } from '@/Actions';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Check, Calendar, Eye, Store, Layers, Package, Pencil } from 'lucide-react';
import { notificationServices, productServices } from '@/Services';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';

const modalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '95vh',
    height: '85vh',
    width: '95vw',
    maxWidth: '1400px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 0 10px rgba(0,0,0,0.15)',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
};

const getTotalDaysMonth = () => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const DayToggleButton = ({ isActive, onClick }) => {
    return (
        <button
            onClick={onClick}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${isActive
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
            title={isActive ? 'Scheduled' : 'Not scheduled'}
        >
            {isActive ? <Check size={15} /> : <X size={15} />}
        </button>
    );
};

const ProductInfoModal = (props) => {
    const { show = false, data = {}, onClose = () => { } } = props;
    return (
        show && (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
                onClick={onClose}
            >
                <div
                    className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Popup Header */}
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800">
                                {data?.groupName}
                            </h3>
                            <span className="text-sm text-gray-500">
                                {data?.containerList?.secondContainerList?.length || 0} products
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Product List */}
                    <div className="overflow-y-auto max-h-[50vh]">
                        {data?.containerList?.secondContainerList?.length > 0 ? (
                            <div className="space-y-3">
                                {data.containerList.secondContainerList.map((product, index) => (
                                    <div
                                        key={product.id || index}
                                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        {product.productImageUrl ? (
                                            <img
                                                src={product.productImageUrl}
                                                alt={product.name || product.productName}
                                                className="w-12 h-12 rounded-lg object-cover"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                                                <span className="text-yellow-600 font-bold text-lg">
                                                    {(product.name || product.productName || 'P').charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-800">
                                                {product.name || product.productName || 'Unknown Product'}
                                            </p>
                                            {product.sku && (
                                                <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                                            )}
                                        </div>
                                        <span className="text-sm font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded">
                                            #{index + 1}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <p className="text-lg">No products added</p>
                                <p className="text-sm">Add products to this group first</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    );
};

const ProductSchedulingTimeModal = (props) => {
    const { groupList = [], setGroupList = () => { }, loading = false } = props;
    const dispatch = useDispatch();
    const { show, data } = useSelector(
        (state) => state.modal.productScheduleTimeModal
    );
    const stores = useSelector((state) => state?.form?.STORES?.data?.LIST);

    const { onConfirm } = data || {};
    const [isGroupNameEdit, setIsGroupNameEdit] = useState(false);
    const totalDays = getTotalDaysMonth();
    const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays]);

    const [scheduleData, setScheduleData] = useState({});
    const [viewProductsModal, setViewProductsModal] = useState({ show: false, group: null });

    const hasGroupsWithNoProducts = useMemo(() => {
        return groupList?.some(group => {
            const productCount = group?.containerList?.secondContainerList?.length || 0;
            return productCount === 0;
        });
    }, [groupList]);

    // Validation: Check if all groups have products
    const isSubmitDisabled = useMemo(() => {
        return loading || groupList?.length === 0 || hasGroupsWithNoProducts;
    }, [loading, groupList, hasGroupsWithNoProducts]);

    const storeOptions = useMemo(() => {
        return stores?.map((store) => ({
            label: store.name,
            value: store.id,
        })) || [];
    }, [stores]);

    useEffect(() => {
        if (groupList?.length > 0) {
            const initialData = {};
            groupList.forEach((group) => {
                initialData[group.id] = {
                    stores: group?.stores || [],
                    days: days.reduce((acc, day) => {
                        acc[day] = group?.days?.includes(day) || false;
                        return acc;
                    }, {}),
                };
            });
            setScheduleData(initialData);
        }
    }, [groupList, days]);


    const handleClose = useCallback(() => {
        dispatch(modalActions.toggleProductScheduleTimeModal(false));
        setScheduleData({});
        setIsGroupNameEdit(false);
    }, [dispatch]);


    const handleRemoveGroup = useCallback((groupToRemove) => {
        setGroupList((prev) => prev.filter((g) => g.id !== groupToRemove.id));
        setScheduleData((prev) => {
            const newData = { ...prev };
            delete newData[groupToRemove.id];
            return newData;
        });
    }, [setGroupList]);

    const handleStoreChange = useCallback((groupId, selectedStores) => {
        setScheduleData((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                stores: selectedStores,
            },
        }));
    }, []);

    const handleDayToggle = useCallback((groupId, day) => {
        setScheduleData((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                days: {
                    ...prev[groupId]?.days,
                    [day]: !prev[groupId]?.days?.[day],
                },
            },
        }));
    }, []);

    const handleViewProducts = useCallback((group) => {
        setViewProductsModal({ show: true, group });
    }, []);

    const closeViewProductsModal = useCallback(() => {
        setViewProductsModal({ show: false, group: null });
    }, []);

    const handleSave = useCallback(async () => {
        // Validate that all groups have stores selected
        const groupsWithoutStores = groupList.filter((group) => {
            const groupSchedule = scheduleData[group.id];
            return !groupSchedule?.stores || groupSchedule.stores.length === 0;
        });

        if (groupsWithoutStores.length > 0) {
            notificationServices?.generateNotification({
                type: 'error',
                message: `Please select stores for: ${groupsWithoutStores.map(g => g.groupName).join(', ')}`,
            });
            return;
        }

        const requestData = groupList.map((group) => {
            const groupSchedule = scheduleData[group.id];
            if (!groupSchedule) return null;

            const activeDays = groupSchedule?.days ? Object?.entries(groupSchedule.days)
                .filter(([_, isActive]) => isActive)
                .map(([day]) => parseInt(day)) : [];

            return {
                groupName: group.groupName,
                productIds: group.containerList?.secondContainerList?.map((item) => item?.id) || [],
                configType: 'dates_of_month',
                configValues: activeDays,
                storeIds: groupSchedule.stores,
                description: '',
            };
        }).filter(Boolean);
        if (onConfirm) {
            try {
                await onConfirm(requestData);
                handleClose();
            } catch (error) {
                console.error('Failed to save:', error);
            }
        }
    }, [groupList, scheduleData, onConfirm, handleClose]);

    const getProductCount = (group) => {
        return group?.containerList?.secondContainerList?.length || 0;
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                key: 'sno',
                title: 'S.NO',
                fixed: true,
                width: 80,
                customCol: true,
            },
            {
                key: 'groupName',
                title: 'GROUP NAME',
                fixed: true,
                width: 220,
                customCol: true,
            },
            {
                key: 'store',
                title: 'STORE',
                width: 200,
                customCol: true,
            },
        ];

        const dayColumns = days.map((day) => ({
            key: `day_${day}`,
            title: day,
            customCol: true,
            width: 65,
        }));

        const actionColumn = [{
            key: 'action',
            title: 'ACTION',
            width: 80,
            customCol: true,
        }];

        return [...baseColumns, ...dayColumns, ...actionColumn];
    }, [days]);

    const colMapping = useMemo(() => ({
        sno: 'sno',
        groupName: 'groupName',
        store: 'store',
        ...days.reduce((acc, day) => {
            acc[`day_${day}`] = `day_${day}`;
            return acc;
        }, {}),
    }), [days]);

    const tableData = useMemo(() => {
        return groupList?.map((group, index) => ({
            id: group.id,
            sno: index + 1,
            groupName: group.groupName,
            productCount: getProductCount(group),
            store: scheduleData[group.id]?.stores || [],
            containerList: group?.containerList,
            ...days.reduce((acc, day) => {
                acc[`day_${day}`] = scheduleData[group.id]?.days?.[day] || false;
                return acc;
            }, {}),
        })) || [];
    }, [groupList, scheduleData, days]);

    const handleEditGroup = useCallback(() => {
        setIsGroupNameEdit((prev) => !prev);
    }, []);


    const handleGroupNameChange = useCallback((value, row) => {
        setGroupList((prev) =>
            prev.map((group) =>
                group.id === row.id
                    ? { ...group, groupName: value }
                    : group
            )
        );
    }, [groupList]);

    const customRowData = useMemo(() => {
        const dayRenderers = days.reduce((acc, day) => {
            acc[`day_${day}`] = (row) => {
                return (
                    <DayToggleButton
                        isActive={scheduleData[row?.id]?.days?.[day] || false}
                        onClick={() => handleDayToggle(row?.id, day)}
                    />
                );
            };
            return acc;
        }, {});

        return {
            sno: (row) => (
                <span className="font-semibold text-gray-700 text-base">{row.sno}</span>
            ),
            groupName: (row) => (
                <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                        {!isGroupNameEdit ? (
                            <span className="text-base font-semibold text-gray-800">
                                {row.groupName}
                            </span>
                        ) : (
                            <input
                                type="text"
                                value={row.groupName}
                                onChange={(e) => handleGroupNameChange(e.target.value, row)}
                                className="text-base border border-gray-300 rounded-md p-2 font-semibold text-gray-800 focus:outline-none focus:border-blue-500"
                            />
                        )}
                        <button
                            onClick={() => handleEditGroup(row)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="Edit Group"
                        >
                            <Pencil size={18} className="text-blue-500 hover:text-blue-600" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full w-fit">
                            {row.productCount || 0} products
                        </span>
                        <button
                            onClick={() => handleViewProducts(row)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="View Products"
                        >
                            <Eye size={18} className="text-blue-500 hover:text-blue-600" />
                        </button>
                    </div>
                </div>
            ),
            store: (row) => {
                return (
                    <CheckPicker
                        data={storeOptions}
                        value={scheduleData[row?.id]?.stores || []}
                        onChange={(value) => handleStoreChange(row?.id, value)}
                        placeholder="Select Stores"
                        style={{ width: 180, fontSize: '14px' }}
                        size="md"
                        searchable={true}
                        cleanable={true}
                        placement="bottomStart"
                        menuStyle={{ zIndex: 9999 }}
                    />
                );
            },
            action: (row) => (
                <div className="flex items-center justify-center">
                    <button
                        onClick={() => handleRemoveGroup(row)}
                        className="p-2 hover:bg-red-100 rounded-full transition-colors"
                        title="Remove Group"
                    >
                        <X size={16} className="text-red-600" />
                    </button>
                </div>
            ),
            ...dayRenderers,
        };
    }, [days, scheduleData, storeOptions, handleStoreChange, handleDayToggle, handleRemoveGroup, handleViewProducts, isGroupNameEdit]);

    // Custom column renderer for day headers
    const customCol = useCallback((column) => {
        if (column.key === 'sno') {
            return (
                <div className="flex flex-row items-center gap-1">
                    <span className="font-semibold text-[13px]">S.NO</span>
                </div>
            );
        }
        if (column.key === 'groupName') {
            return (
                <div className="flex flex-row items-center gap-1">
                    <Package size={16} className="text-yellow-600" strokeWidth={2} />
                    <span className="font-semibold text-[13px]">GROUP NAME</span>
                </div>
            );
        }
        if (column.key === 'store') {
            return (
                <div className="flex flex-row items-center gap-1">
                    <Store size={16} className="text-yellow-600" strokeWidth={2} />
                    <span className="font-semibold text-[13px]">STORE</span>
                </div>
            );
        }
        if (column.key === 'action') {
            return (
                <div className="flex flex-row items-center gap-1">
                    <span className="font-semibold text-[13px]">ACTION</span>
                </div>
            );
        }
        if (column.key.startsWith('day_')) {
            const day = parseInt(column.key.split('_')[1]);
            return (
                <div className="flex flex-row items-center gap-1">
                    <Calendar size={14} className="text-yellow-600" strokeWidth={2} />
                    <span className="font-semibold text-[13px]">{day}</span>
                </div>
            );
        }
        return column.title;
    }, []);

    return (
        <>
            <Modal open={show} onClose={handleClose}>
                <div style={modalStyles}>
                    <ProductInfoModal show={viewProductsModal?.show} data={viewProductsModal?.group} onClose={closeViewProductsModal} />
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Add Schedule</h2>
                            {hasGroupsWithNoProducts && (
                                <p className="text-sm text-red-500 mt-1">
                                    ⚠️ Some groups have no products selected. Please add products to all groups.
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Table Container */}
                    <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg" style={{ height: '70vh' }}>
                        <VariantTable
                            data={tableData}
                            columns={columns}
                            colMapping={colMapping}
                            customRowData={customRowData}
                            customCol={customCol}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSubmitDisabled}
                            className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <span className="animate-spin">⏳</span>
                                    Saving...
                                </>
                            ) : (
                                'Save Schedule'
                            )}
                        </button>
                    </div>

                    {/* Custom scrollbar styles */}
                    <style>
                        {`
                        .h-full.w-full.overflow-auto::-webkit-scrollbar {
                            height: 8px;
                            width: 8px;
                        }
                        .h-full.w-full.overflow-auto::-webkit-scrollbar-track {
                            background: #fef9c3;
                            border-radius: 10px;
                        }
                        .h-full.w-full.overflow-auto::-webkit-scrollbar-thumb {
                            background: #facc15;
                            border-radius: 10px;
                        }
                        .h-full.w-full.overflow-auto::-webkit-scrollbar-thumb:hover {
                            background: #eab308;
                        }
                    `}
                    </style>
                </div>

            </Modal>
        </>
    );
};

export default ProductSchedulingTimeModal;